import React, { useState, useEffect, useRef, useDeferredValue } from 'react';
import { Alert, Button, Badge, Card, Table } from 'react-bootstrap';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { useBrand, useAuth } from '../context/AuthContext';
import { useUI } from '../context/UIContext';
import { auditService } from '../services/auditService';
import AccessDenied from '../components/common/AccessDenied';
import ModifyBenefitModal from '../components/reports/ModifyBenefitModal';
import BenefitStatusBadge from '../utils/BenefitStatusBadge';
import { getFundTypeLabel } from '../utils/fundTypeUtils';
import { formatDate } from '../utils/formatters';
import { buildBenefitReportHeaders, REPORT_TYPE } from '../utils/benefitReportHeaders';
import { FUND_TYPES, BENEFIT_STATUS, PAGE_STATUS } from '../constants/constants';
import { getReferralSourceLabel } from '../constants/constants';

/**
 * NOTE ON INITIAL LOAD BEHAVIOR
 *
 * This page renders a large audit table that is usually 100+ rows.
 * On a full browser refresh, React must synchronously render the page
 * before the browser can paint, which can cause the global loading spinner
 * to briefly flash instead of staying visible.
 *
 * The save/update path correctly shows the loading spinner for the full
 * duration because the page is already rendered.
 *
 * Fully resolving the initial refresh flash requires pagination,
 * virtualization, or server-side paging, which is intentionally out of
 * scope for this frontend-only rewrite.
 */
const BenefitAuditReportPage = () => {
  const { isCNG } = useBrand();
  const { isAdmin } = useAuth();

  if (!isAdmin) {
    return <AccessDenied className="audit-report-page" />;
  }

  const { isInitialized, showLoading, hideLoading } = useUI();
  const hasLoadedRef = useRef(false);
  const [canRenderTable, setCanRenderTable] = useState(false);
  const [data, setData] = useState(null);
  const deferredData = useDeferredValue(data);
  const [error, setError] = useState('');
  const [submitStatus, setSubmitStatus] = useState(null);
  const [submitErrors, setSubmitErrors] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [selectedBenefit, setSelectedBenefit] = useState(null);

  const loadAuditReport = async (
    message = 'Loading audit report...'
  ) => {
    showLoading(message);
    setError('');

    try {
      const response = await auditService.getAuditReport();
      if (response.success) {
        setData({ benefits: response.benefits || [] });
      } else {
        setError(response.error || 'Failed to load audit report.');
      }
    } catch (err) {
      console.error('Error loading audit report:', err);
      setError('An error occurred while loading the audit report.');
    }
  };

  useEffect(() => {
    if (isInitialized && isAdmin && !hasLoadedRef.current) {
      hasLoadedRef.current = true;
      // Spinner may briefly flash on full page refresh due to heavy initial render.
      loadAuditReport();
    }
  }, [isInitialized, isAdmin]);

  useEffect(() => {
    if (data) {
      // Let the browser paint first
      requestAnimationFrame(() => {
        setCanRenderTable(true);
        hideLoading();
      });
    }
  }, [data, hideLoading]);

  const handleModifyClick = (benefit) => {
    setSelectedBenefit(benefit);
    setShowModal(true);
  };

  const handleModalClose = () => {
    setShowModal(false);
    setSelectedBenefit(null);
  };

  const handleBenefitUpdate = async (id, updateData) => {
    showLoading('Saving benefit...');

    try {
      const response = await auditService.updateBenefit(id, updateData);
      handleModalClose();

      if (response.success) {
        await loadAuditReport('Loading audit report...');
        return { success: true };
      }

      return {
        success: false,
        errors: response.validationErrors || [response.error]
      };
    } catch (err) {
      console.error('Error updating benefit:', err);
      return {
        success: false,
        errors: ['An error occurred while updating the benefit.']
      };
    }
  };

  const handleSubmitAudit = async () => {
    // Check if there are any approved or rejected benefits
    const submittable = data.benefits.filter(b =>
      b.status === BENEFIT_STATUS.APPROVED || b.status === BENEFIT_STATUS.AUTOAPPROVED || b.status === BENEFIT_STATUS.REJECTED
    );

    if (submittable.length === 0) {
      setSubmitStatus(PAGE_STATUS.WARN);
      setSubmitErrors(['There are no benefits eligible for submission.']);
      return;
    }

    // Download CSV before submitting
    downloadCSV();

    setSubmitting(true);
    setSubmitStatus(null);
    setSubmitErrors([]);

    try {
      const response = await auditService.submitAudit();
      if (response.success) {
        setSubmitStatus(response.pageStatus);
        if (response.pageStatus === PAGE_STATUS.SUCCESS) {
          await loadAuditReport();
        } else if (response.validationErrors) {
          setSubmitErrors(response.validationErrors);
        }
      } else {
        setSubmitStatus(PAGE_STATUS.FAIL);
        setSubmitErrors(response.validationErrors || [response.error]);
      }
    } catch (err) {
      console.error('Error submitting audit:', err);
      setSubmitStatus(PAGE_STATUS.FAIL);
      setSubmitErrors(['An error occurred while submitting the audit report.']);
    } finally {
      setSubmitting(false);
    }
  };

  const downloadCSV = () => {
    // Filter to only approved/rejected benefits
    const submittable = data.benefits.filter(b =>
      b.status === BENEFIT_STATUS.APPROVED || b.status === BENEFIT_STATUS.AUTOAPPROVED || b.status === BENEFIT_STATUS.REJECTED
    );

    if (submittable.length === 0) return;

    // Build CSV headers
    const headers = buildBenefitReportHeaders(REPORT_TYPE.AUDIT, isCNG);

    // Build CSV rows
    const rows = submittable.map(b => {
      const fundTypeExport = b.caresExceptionRequestFlg ? 'CARES - Additional Grant' : getFundTypeLabel(b.fundType);

      let row = [
        b.status,
        b.agencyEmail || '',
        formatDate(b.createTstamp),
        formatDate(b.updateTstamp),
        b.agencyName || '',
        b.submissionType || '',
        b.lastName || '',
        b.firstName || '',
        b.accountId || '',
        b.street || '',
        b.city || '',
        b.state || '',
        b.zip || '',
        fundTypeExport,
        b.pledgeAmount || ''
      ];
      if (isCNG) {
        row = [...row,
          b.fpl || '',
          b.smi || '',
          b.ami || '',
          b.energyDiscount || '',
          b.arrearage || '',
          b.fundType === FUND_TYPES.CARES ? (b.weatherizationProgramInterest ? 'Yes' : 'No') : '',
          getReferralSourceLabel(b.referralSource) || ''
        ];
      }
      row = [...row, b.additionalInformation || '', b.reasonForChange || ''];
      return row;
    });

    // Create CSV content
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    // Download file
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    const dateStr = formatDate(new Date()).replace(/\//g, '-');
    a.href = url;
    a.download = `AuditReport_${dateStr}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const getFundTypeDisplay = (benefit) => {
    return (
      <div>
        <div>{getFundTypeLabel(benefit.fundType)}</div>
        {benefit.caresExceptionRequestFlg && (
          <Badge bg="success" className="mt-1">Additional Grant</Badge>
        )}
      </div>
    );
  };

  if (!isAdmin) {
    return <AccessDenied className="audit-report-page" />;
  }

  return (
    <div id="audit-report-page">
      <div className="d-flex justify-content-center mb-3">
          <h2>Audit Report</h2>
        </div>

        {/* Submit status alerts */}
        {submitStatus === PAGE_STATUS.SUCCESS && (
          <Alert variant="success" dismissible onClose={() => setSubmitStatus(null)}>
            Audit report submitted successfully.
          </Alert>
        )}
        {submitStatus === PAGE_STATUS.WARN && (
          <Alert variant="warning" dismissible onClose={() => setSubmitStatus(null)}>
            {submitErrors.length > 0 ? submitErrors[0] : 'Audit report submitted with warnings.'}
          </Alert>
        )}
        {submitStatus === PAGE_STATUS.FAIL && (
          <Alert variant="danger">
            <strong>Audit report not submitted</strong>
            {submitErrors.length > 0 && (
              <ul className="mb-0 mt-2">
                {submitErrors.map((err, idx) => (
                  <li key={idx}>{err}</li>
                ))}
              </ul>
            )}
          </Alert>
        )}

        {error && (
          <Alert variant="danger">{error}</Alert>
        )}

        {canRenderTable && deferredData && deferredData?.benefits && (
          <>
            <Card className="shadow">
              <Card.Header>
                <div className="d-flex justify-content-end">
                  <Button
                    variant="primary"
                    onClick={handleSubmitAudit}
                    disabled={submitting}>
                    {submitting ? (
                      <LoadingSpinner inline message="Submitting..." />
                    ) : (
                      'Submit'
                    )}
                  </Button>
                </div>
              </Card.Header>
              <Card.Body className="p-0">
              <Table size="sm" hover responsive bordered>
                  <thead>
                    <tr>
                      <th>Status</th>
                      <th></th>
                      <th>Agency Email</th>
                      <th>Date Entered</th>
                      <th>Audit Date</th>
                      <th>Agency Name</th>
                      <th>New/Corrected</th>
                      <th>Last Name</th>
                      <th>First Name</th>
                      <th>Account Number</th>
                      <th>Service Address</th>
                      <th>Town</th>
                      <th>State</th>
                      <th>Zip</th>
                      <th>Fund</th>
                      <th>Grant</th>
                      {isCNG && (
                        <>
                          <th>FPL</th>
                          <th>SMI</th>
                          <th>AMI</th>
                          <th>Energy Discount</th>
                          <th>Arrearage Mgmt</th>
                          <th>Weatherization Interest</th>
                          <th>Referral Source</th>
                        </>
                      )}
                      <th>Additional Information</th>
                      <th>Reason for Change</th>
                    </tr>
                  </thead>
                  <tbody>
                    {deferredData.benefits.map((benefit, index) => (
                      <tr key={benefit.id || index}>
                        <td><BenefitStatusBadge status={benefit.status} /></td>
                        <td>
                          <Button
                            variant="warning"
                            size="sm"
                            onClick={() => handleModifyClick(benefit)}
                          >
                            Modify
                          </Button>
                        </td>
                        <td>{benefit.agencyEmail}</td>
                        <td>{formatDate(benefit.createTstamp)}</td>
                        <td>{formatDate(benefit.updateTstamp)}</td>
                        <td>{benefit.agencyName}</td>
                        <td>{benefit.submissionType}</td>
                        <td>{benefit.lastName}</td>
                        <td>{benefit.firstName}</td>
                        <td>{benefit.accountId}</td>
                        <td>{benefit.street}</td>
                        <td>{benefit.city}</td>
                        <td>{benefit.state}</td>
                        <td>{benefit.zip}</td>
                        <td>{getFundTypeDisplay(benefit)}</td>
                        <td>{benefit.pledgeAmount}</td>
                        {isCNG && (
                          <>
                            <td>{benefit.fpl}</td>
                            <td>{benefit.smi}</td>
                            <td>{benefit.ami}</td>
                            <td>{benefit.energyDiscount}</td>
                            <td>{benefit.arrearage}</td>
                            <td>
                              {benefit.fundType === FUND_TYPES.CARES && (
                                benefit.weatherizationProgramInterest ? 'Yes' : 'No'
                              )}
                            </td>
                            <td>{getReferralSourceLabel(benefit.referralSource)}</td>
                          </>
                        )}
                        <td>{benefit.additionalInformation}</td>
                        <td>{benefit.reasonForChange}</td>
                      </tr>
                    ))}
                    {deferredData.benefits.length === 0 && (
                      <p className="text-muted">All benefits have been submitted.</p>
                    )}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </>
      )}

      {/* Modify Benefit Modal */}
      {showModal && selectedBenefit && (
        <ModifyBenefitModal
          benefit={selectedBenefit}
          onClose={handleModalClose}
          onSave={handleBenefitUpdate}
        />
      )}
    </div>
  );
};

export default BenefitAuditReportPage;
